import React, { useCallback, useEffect, useState, useMemo, useRef, forwardRef } from 'react';
import { fileValidator } from '../files/utils';
import { useToast } from 'shared/toast';
import { isNil, get } from 'lodash';
import heic2any from 'heic2any';
import { buildFileTypesList, defaultFileSizeMBLimit } from 'shared/../constants/attachments';

const preventBrowserDefaults = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

const onDragEnter = (e) => {
  preventBrowserDefaults(e);
};

/**
 * @typedef {Object} FileUploaderProps
 * @property {string} [dataTestId] Data test Id.
 * @property {number} [filesLimit={1}] The amount of files allowed to upload. 1 by default.
 * @property {number} [fileSizeMBLimit] The limit of a file size in MB allowed to upload.
 * @property {Array<string>} [allowedFileTypes] The list of file types allowed to upload.
 * @property {boolean} [imagesOnly={false}] Indicates if images files only are allowed to upload. False by default.
 * @property {boolean} [allowPdf={true}] Indicates if PDF files are allowed. Used if imagesOnly is true. True by default.
 * @property {boolean} [disabled={false}] Indicates if the component is disabled. False by default.
 * @property {Function} onUpload A callback when new files are uploaded.
 * @property {React.FunctionComponent} Component A component to be used to display the file uploader. Should implement UI/UX of uploading process.
 * @property {Function} [onChange] A callback when text content of the Component is changed.
 * @property {boolean} [isLoading={false}] Indicates if Component is loading. False by default.
 * @property {Function} [setIsLoading] A callback when new files are uploading.
 *
 * @param {FileUploaderProps} props
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement}
 */
const FileUploader = ({
  filesLimit = 1,
  fileSizeMBLimit = defaultFileSizeMBLimit,
  allowedFileTypes,
  imagesOnly = false,
  allowPdf = true, // For images only
  onUpload,
  // Component to display uploader
  Component,
  // On change text content to handle upload by copy/paste file data
  onChange: onCustomChange,
  // Rest props to be passed to Component
  disabled = false,
  isLoading = false,
  dataTestId,
  setIsLoading, // Set client's flag
  ...props
}, ref) => {
  // Build allowed file types
  const fileTypes = useMemo(() => {
    if (allowedFileTypes && allowedFileTypes.length > 0) {
      return allowedFileTypes;
    }
    return buildFileTypesList({ imagesOnly, allowPdf });
  }, [allowedFileTypes, imagesOnly, allowPdf]);

  const toast = useToast();

  const hiddenFileInput = useRef(null);

  // Uploading state
  const [uploadCount, setUploadCount] = useState(0);
  const [filesData, setFilesData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (filesData.length !== uploadCount || uploadCount === 0) {
      return;
    }
    onUpload(filesData);
    setFilesData([]);
    setUploadCount(0);
    if (setIsLoading) {
      setIsLoading(false);
    }
    setIsUploading(false);
  }, [filesData, uploadCount]);

  const onClick = useCallback(() => {
    hiddenFileInput.current.click();
  }, []);

  const onDragOver = useCallback((e) => {
    preventBrowserDefaults(e);
    setActive(true);
  }, []);

  const onDragLeave = useCallback((e) => {
    preventBrowserDefaults(e);
    setActive(false);
  }, []);

  const uploadFiles = useCallback((files) => {
    if (disabled) {
      toast.info({ message: 'File uploading is disabled' });
      return;
    }
    if (setIsLoading) {
      setIsLoading(true);
    }
    setIsUploading(true);
    const validationResults = fileValidator(files, {
      fileSizeMBLimit,
      filesLimit,
      allowedFileFormats: fileTypes,
    });
    const validFiles = [];
    let errorToShow = null;

    if (validationResults && validationResults.length) {
      validationResults.forEach(({ isValidFile, errVal }, i) => {
        if (isValidFile) {
          validFiles.push(files[i]);
        } else if (isNil(errorToShow) && errVal) {
          errorToShow = errVal;
        }
      });
    } else {
      const { isValidFile, errVal } = validationResults;
      if (isValidFile) {
        validFiles.push(files);
      }
      errorToShow = isValidFile ? null : errVal;
    }

    if (errorToShow) {
      toast.error({ message: 'Upload Issue.', error: errorToShow });
    }
    if (validFiles.length > 0) {
      setUploadCount(validFiles.length);
      validFiles.forEach((file) => {
        const { name, type } = file;
        const fileNameExt = name.substr(name.lastIndexOf('.') + 1);

        // Convert heic/heif to JPEG - majority of browsers does not support heic rendering (except Apple products)
        const isHEIC = (type === '' && (fileNameExt.toLowerCase() === 'heic' || fileNameExt.toLowerCase() === 'heif'))
          || (type && (type.toLowerCase().includes('heic') || type.toLowerCase().includes('heif')));

        if (isHEIC) {
          const blob = URL.createObjectURL(file);

          fetch(blob)
            .then((res) => res.blob())
            .then((blob) => heic2any({ blob, toType: 'image/jpeg' }))
            .then((conversionResult) => {
              const reader = new FileReader();
              reader.readAsDataURL(conversionResult);
              reader.onload = (e) => {
                const fileData = e.target.result.split(',')[1];
                setFilesData((prev) => [...prev, { name, fileType: 'image/jpeg', data: fileData }]);
              };
              return null;
            })
            .catch((err) => {
              console.error('Failed to convert HEIC/HEIF to JPEG', err);
              // Upload as it is
              const reader = new FileReader();
              reader.readAsDataURL(file);
              reader.onload = (e) => {
                const fileData = e.target.result.split(',')[1];
                setFilesData((prev) => [...prev, { name, fileType: type, data: fileData }]);
              };
            });
          return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (e) => {
          const fileData = e.target.result.split(',')[1];
          setFilesData((prev) => [...prev, { name, fileType: type, data: fileData }]);
        };
      });
    } else {
      if (setIsLoading) {
        setIsLoading(false);
      }
      setIsUploading(false);
    }
  }, [disabled, fileTypes, filesLimit, fileSizeMBLimit]);

  const onChange = useCallback(({ target: { files } }) => uploadFiles(files), []);

  const onDrop = useCallback((e) => {
    preventBrowserDefaults(e);
    setActive(false);
    if (disabled) {
      toast.info({ message: 'File uploading is disabled' });
      return;
    }
    const draggedFiles = get(e, 'dataTransfer.files');
    uploadFiles(draggedFiles);
  }, [disabled]);

  // to parse images pasted as html img tag
  const onComponentChange = useCallback((e) => {
    const { value: eValue, target: { value: targetValue } = {} } = e;
    const value = eValue ?? targetValue;

    const startImage = '<img src="data:image/';

    if (value.includes(startImage)) {
      preventBrowserDefaults(e);
      if (setIsLoading) {
        setIsLoading(true);
      }
      setIsUploading(true);
      // Start index
      const startIndex = value.indexOf(startImage) + startImage.length;
      const cutStartString = value.slice(startIndex);

      // End index
      const endIndexWithAlt = cutStartString.indexOf('" alt="image"');
      const endIndex = endIndexWithAlt > 0 ? endIndexWithAlt : cutStartString.indexOf('">');

      if (endIndex > -1) {
        // File data
        const [fileType, data] = cutStartString.substring(0, endIndex).split(';base64,');
        setUploadCount(1);
        setFilesData([{ name: `image.${fileType}`, fileType, data }]);
      }
      return;
    }
    if (onCustomChange) {
      onCustomChange(e);
    }
  }, []);

  const onPaste = useCallback((e) => {
    if (disabled) {
      toast.info({ message: 'File uploading is disabled' });
      return;
    }
    if (e.clipboardData?.files.length > 0) {
      preventBrowserDefaults(e);
      const clipboardFiles = get(e, 'clipboardData.files');
      uploadFiles(clipboardFiles);
    }

    if (e.nativeEvent && e.nativeEvent?.clipboardData?.files.length > 0) {
      const nEClipboardFiles = get(e, 'nativeEvent.clipboardData.files');
      uploadFiles(nEClipboardFiles);
    }
  }, [disabled]);

  return (
    <>
      <Component
        isUploading={isUploading || isLoading}
        active={active}
        onClick={onClick}
        onDragOver={onDragOver}
        onDragEnter={onDragEnter}
        onDrop={onDrop}
        onDragLeave={onDragLeave}
        onPaste={onPaste}
        onChange={onComponentChange}
        data-testid={dataTestId}
        ref={ref}
        {...props}
      />
      <input
        type="file"
        ref={hiddenFileInput}
        onChange={onChange}
        disabled={disabled}
        style={{ display: 'none' }}
        value=""
        multiple={filesLimit > 1}
      />
    </>
  );
};

/**
 * A file uploader component with various options and handlers.
 *
 * @param {FileUploaderProps} props Component props.
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement} File uploader component.
 */
const ForwardFileUploader = forwardRef(FileUploader);

export default ForwardFileUploader;
